/**
 * Scripted letter drafts for the renewals tab. Authored for the agreements in
 * the demo story and generated from contract data for the rest.
 */
import type { ScriptedStep } from '../../components/useScriptedSteps';
import { COMPANY } from '../../data/company';
import { getContract, noticeDeadline } from '../../data/contracts';
import { fmtDate, parseIsoDate, startOfToday } from '../../data/dates';
import { getSupplier } from '../../data/suppliers';
import type { Contract, ContractId, Supplier } from '../../data/types';
import { titleInSentence } from './agreements';
import type { RenewalEventKind } from './renewals';

/** Steps are the same for every letter so one hook can serve every row. */
export const LETTER_STEPS: ScriptedStep[] = [
  {
    label: 'Reading the agreement and its notice requirements',
    durationMs: 700,
  },
  { label: 'Checking delivery method and signatory', durationMs: 700 },
  { label: 'Drafting the letter', durationMs: 800 },
];

export interface DraftLetter {
  /** Dialog title, e.g. "Notice of non-renewal". */
  heading: string;
  /** How the letter must be delivered to be effective. */
  delivery: string;
  to: string[];
  subject: string;
  paragraphs: string[];
  signer: { name: string; title: string };
  /** Plain text for the copy button. */
  text: string;
}

const BUYER_NAME = COMPANY.legalName;

/** City line for a supplier address block, e.g. "Seattle, WA" or "Osaka, Japan". */
function cityLine(supplier: Supplier): string {
  const { city, country } = supplier.hq;
  return country === 'United States' ? city : `${city}, ${country}`;
}

function splitSigner(signedBy: string): { name: string; title: string } {
  const [name, ...rest] = signedBy.split(', ');
  return { name: name ?? signedBy, title: rest.join(', ') };
}

function assemble(letter: Omit<DraftLetter, 'text'>): DraftLetter {
  const text = [
    fmtDate(startOfToday()),
    '',
    ...letter.to,
    '',
    `Re: ${letter.subject}`,
    '',
    ...letter.paragraphs.flatMap((paragraph) => [paragraph, '']),
    'Sincerely,',
    letter.signer.name,
    letter.signer.title,
    BUYER_NAME,
  ].join('\n');
  return { ...letter, text };
}

function agreementRef(contract: Contract): string {
  return `${titleInSentence(contract.title)} ${contract.id}`;
}

function stratusNotice(contract: Contract): DraftLetter {
  return assemble({
    heading: 'Notice of non-renewal',
    delivery:
      'Deliver by hand or overnight courier to the Stratus General Counsel. Email alone is not effective (Section 11.2).',
    to: [
      'General Counsel',
      getSupplier(contract.supplierId).name,
      cityLine(getSupplier(contract.supplierId)),
    ],
    subject: `Notice of non-renewal under Section 5.2, ${contract.id}`,
    paragraphs: [
      `${BUYER_NAME} gives notice under Section 5.2 of the Enterprise Cloud Agreement ${contract.id} that it will not renew the Agreement at the end of the Initial Term on January 31, 2027. This notice is delivered at least ninety (90) days before the end of the Initial Term and in the manner required by Section 11.2.`,
      `${COMPANY.name} wishes to continue the relationship on terms that fit current consumption, which is tracking 18% under the Annual Committed Spend. We request the commitment review provided by Section 3.4 within thirty (30) days and propose a lower commitment with a ramp, as set out in the attached term sheet.`,
      `This notice does not waive any right of ${COMPANY.name} under the Agreement, including the fee credit due under Section 6.2 for September 2026 (monthly availability of 99.81% against the 99.95% commitment), which Section 6.3 requires Stratus to apply to the next invoice without a claim.`,
    ],
    signer: splitSigner(contract.signedBy.buyer),
  });
}

function halbrookCloseOut(contract: Contract): DraftLetter {
  return assemble({
    heading: 'Notice of statement of work close-out',
    delivery: 'Send by email and overnight courier to the engagement partner.',
    to: [
      splitSigner(contract.signedBy.supplier).name,
      splitSigner(contract.signedBy.supplier).title,
      getSupplier(contract.supplierId).name,
    ],
    subject: `Close-out of ${contract.id} and billing against the budget cap`,
    paragraphs: [
      `${BUYER_NAME} confirms that ${contract.id} will end on November 30, 2026 and will not be extended. The final report, roadmap and executive presentation remain due by November 13, 2026 under Section 3.1.`,
      `Fees and expenses billed to date total $468,200 against the $450,000 budget cap in Section 5.2. ${COMPANY.name} has approved no change order, and is not obliged to pay any amount above $450,000. We will pay invoice HP-INV-55102 only up to that amount, which is $78,130, and dispute $18,200: $4,400 for a Senior Partner rate that is not among the agreed hourly rates (Section 4.2), $3,380 of expenses above the 10% limit in Section 5.3, and the remaining $10,420 above the budget cap once those two are removed.`,
      'Please send a corrected invoice, and confirm that no further work will be performed or billed after November 30, 2026 without a signed change order.',
    ],
    signer: splitSigner(contract.signedBy.buyer),
  });
}

function corvaneRenewalDecision(contract: Contract): DraftLetter {
  return assemble({
    heading: 'Renewal decision and proposed share of volume',
    delivery: `Send by email and courier to the ${splitSigner(contract.signedBy.supplier).title}.`,
    to: [
      splitSigner(contract.signedBy.supplier).name,
      splitSigner(contract.signedBy.supplier).title,
      getSupplier(contract.supplierId).name,
      cityLine(getSupplier(contract.supplierId)),
    ],
    subject: `Renewal of ${contract.id} beyond December 31, 2026`,
    paragraphs: [
      `${BUYER_NAME} wishes to continue to buy copper building wire from Corvane after ${contract.id} expires on December 31, 2026, under a replacement agreement. This letter gives the renewal decision referred to in Section 2.2.`,
      'We propose to place 37% of our copper building wire volume with Corvane from January 1, 2027, with the balance placed with other qualified suppliers. We ask Corvane to hold the 2026 price of $424.36 per 100 feet for 2027, to reset the volume rebate threshold in Section 3.5 so that it still applies at the lower volume, and to confirm in writing the notice of its credit rating downgrade required by Section 7.3.',
      'We would like to meet within two weeks to agree the terms, and welcome Corvane’s proposal on quality performance against the limit of 500 defects per million in Section 8.1.',
    ],
    signer: splitSigner(contract.signedBy.buyer),
  });
}

function genericNonRenewal(contract: Contract): DraftLetter {
  const deadline = noticeDeadline(contract);
  const deadlineText = deadline ? fmtDate(deadline) : 'the notice date';
  const supplier = getSupplier(contract.supplierId);
  return assemble({
    heading: 'Notice of non-renewal',
    delivery:
      'Send by email and overnight courier to the signatory at the supplier.',
    to: [
      splitSigner(contract.signedBy.supplier).name,
      splitSigner(contract.signedBy.supplier).title,
      supplier.name,
    ],
    subject: `Notice of non-renewal, ${contract.id}`,
    paragraphs: [
      `${BUYER_NAME} gives notice that it does not intend to renew the ${agreementRef(contract)} at the end of its current term on ${fmtDate(parseIsoDate(contract.endDate))}. This notice is given on or before ${deadlineText}, the last day for notice of non-renewal.`,
      `${BUYER_NAME} would welcome the opportunity to agree revised terms before the current term ends, and asks ${supplier.shortName} to propose them within thirty (30) days. This notice does not waive any right under the agreement.`,
    ],
    signer: splitSigner(contract.signedBy.buyer),
  });
}

function genericExtension(contract: Contract): DraftLetter {
  const supplier = getSupplier(contract.supplierId);
  return assemble({
    heading: 'Request to extend or replace',
    delivery: 'Send by email to the signatory at the supplier.',
    to: [
      splitSigner(contract.signedBy.supplier).name,
      splitSigner(contract.signedBy.supplier).title,
      supplier.name,
    ],
    subject: `Extension or replacement of ${contract.id}`,
    paragraphs: [
      `The ${agreementRef(contract)} ends on ${fmtDate(parseIsoDate(contract.endDate))} and does not renew automatically. ${BUYER_NAME} would like to discuss an extension or replacement agreement, and asks ${supplier.shortName} to share its proposal, including pricing, term and service levels, within thirty (30) days.`,
      'Any extension must be agreed in a written amendment signed by both parties. Until one is signed, the current terms continue to apply and this letter does not waive any right under the agreement.',
    ],
    signer: splitSigner(contract.signedBy.buyer),
  });
}

/** The draft shown for a renewal event's "Draft notice letter" action. */
export function buildLetter(
  contractId: ContractId,
  kind: RenewalEventKind,
): DraftLetter {
  const contract = getContract(contractId);
  if (contractId === 'EA-STRT-2024-001') return stratusNotice(contract);
  if (contractId === 'SOW-HALB-2026-03') return halbrookCloseOut(contract);
  if (contractId === 'SA-CORV-2024-017')
    return corvaneRenewalDecision(contract);
  return kind === 'notice_deadline'
    ? genericNonRenewal(contract)
    : genericExtension(contract);
}
